import React, { useState, useEffect, useRef } from 'react';

const TOUR_STEPS = [
  { section: '#hero', label: 'The Person', duration: 5000, narration: "I'm Mekhal — a data systems architect who builds things that matter. Physics undergrad turned fintech builder, currently running two startups while consulting full-time." },
  { section: '#story', label: 'The Arc', duration: 6000, narration: "My career started in AML compliance at Western Union — where a bad query costs millions. That made me meticulous. Then Citizens Bank: 800GB/day pipelines, 200+ datasets, 40,000-customer migrations." },
  { section: '#experience', label: 'The Work', duration: 6000, narration: "At Forte Consulting, I cut query runtimes by 40% and eliminated a third of manual operational work. Meanwhile, I built Finide — an AI financial forecasting platform — from zero to production in four months." },
  { section: '#projects', label: 'The Ventures', duration: 6000, narration: "Six products shipped in six months while consulting full-time — Finide, Memories, CareHelp, SMART, Boston Neighborhood Intelligence, and two geospatial snow tools. Finide is live and accepted to Founder University and LAUNCH." },
  { section: '#skills', label: 'The Stack', duration: 5000, narration: "I work across the full data and AI stack — from Redshift and dbt to Claude, Groq, Gemini, and Deepgram. React, React Native, Next.js, TypeScript on the frontend. I don't just use these tools. I build production systems with them." },
  { section: '#contact', label: 'The Invitation', duration: 4000, narration: "I'm open to senior roles across the US — hybrid, remote, or in person. Fintech and AI infrastructure are where I do my best work. If that matches what you're building — let's talk." },
];

export default function HireMe60({ active, onClose }) {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    setStep(0); setProgress(0);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const cur = TOUR_STEPS[step];
    document.querySelector(cur.section)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    let elapsed = 0;
    const interval = 50;
    progressRef.current = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / cur.duration) * 100);
      if (elapsed >= cur.duration) {
        clearInterval(progressRef.current);
        if (step < TOUR_STEPS.length - 1) setStep(s => s + 1);
        else onClose();
      }
    }, interval);
    return () => clearInterval(progressRef.current);
  }, [step, active]);

  if (!active) return null;

  const cur = TOUR_STEPS[step];
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1500, pointerEvents: 'none' }}>
      {/* Overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,10,0.5)', backdropFilter: 'blur(2px)' }} />

      {/* Panel */}
      <div style={{
        position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)',
        width: 'min(600px, 90vw)', background: 'var(--ink)',
        padding: '28px 32px', pointerEvents: 'all',
        border: '1px solid rgba(232,197,71,0.2)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
      }}>
        {/* Progress bar */}
        <div style={{ height: '2px', background: 'rgba(250,250,248,0.1)', marginBottom: '20px' }}>
          <div style={{ height: '100%', background: 'var(--accent)', width: `${progress}%`, transition: 'width 0.05s linear' }} />
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {TOUR_STEPS.map((s, i) => (
            <div key={i} style={{ flex: 1, height: '2px', background: i <= step ? 'var(--accent)' : 'rgba(250,250,248,0.1)' }} />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.25em', color: 'var(--accent)', textTransform: 'uppercase' }}>
            {String(step + 1).padStart(2, '0')} / {String(TOUR_STEPS.length).padStart(2, '0')} · {cur.label}
          </div>
          <button onClick={onClose} style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'rgba(250,250,248,0.3)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.1em' }}>ESC</button>
        </div>

        <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontStyle: 'italic', color: '#fafaf8', lineHeight: 1.6, fontWeight: 300 }}>
          "{cur.narration}"
        </div>
      </div>
    </div>
  );
}

export function HireMeButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase',
      color: 'var(--ink)', background: 'var(--accent)', border: 'none',
      padding: '12px 24px', cursor: 'crosshair', display: 'flex', alignItems: 'center', gap: '10px',
      transition: 'opacity 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      <span>▶</span> Hire Me in 60 Seconds
    </button>
  );
}
